import React, { useEffect, useRef, useState } from "react";
import { LeadSignalType, leadTypeMeta } from "../lib/leadTypeMeta";

// The lead-type filter as checkboxes (was a single-choice select), so several
// signal types can be layered: a property shows if it carries ANY checked type.
// Reuses the Leads buy box's dropdown styles (.bbx-dd*).

interface Props {
  /** Checked lead types; empty = all. */
  value: string[];
  onChange: (next: string[]) => void;
  options: LeadSignalType[];
}

const LeadTypeMultiSelect: React.FC<Props> = ({ value, onChange, options }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on an outside click or Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const toggle = (t: string) =>
    onChange(value.includes(t) ? value.filter((v) => v !== t) : [...value, t]);

  // One checked type reads as its name, several as a count, none as "All".
  const label =
    value.length === 0 ? "All Lead Types" : value.length === 1 ? leadTypeMeta(value[0]).label : `${value.length} lead types`;

  return (
    <div className="bbx-dd" ref={ref}>
      <button
        type="button"
        className={"lv-chip bbx-dd-btn" + (open ? " bbx-dd-btn--open" : "") + (value.length ? " bbx-dd-btn--on" : "")}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label="Filter leads by type"
        title="Filter leads by type. Check several to see any of them."
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <span className="bbx-dd-caret" aria-hidden="true">
          ▾
        </span>
      </button>
      {open && (
        <div className="bbx-dd-panel" role="group" aria-label="Lead types">
          {options.map((t) => (
            <label key={t} className="bbx-dd-opt">
              <input type="checkbox" checked={value.includes(t)} onChange={() => toggle(t)} />
              <span>{leadTypeMeta(t).label}</span>
            </label>
          ))}
          {value.length > 0 && (
            <button type="button" className="map-buybox-clear" onClick={() => onChange([])}>
              Clear lead types
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadTypeMultiSelect;
